import React, { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { ReviewsModuleProps } from '../types/review.types';
import './ReviewsModule.css';
import { publicationReviewStats, publicationReviews } from '../data/publicationReviews';

const ReviewsModule: React.FC<ReviewsModuleProps> = ({
    title = 'The reviews are coming in.',
    stats = publicationReviewStats,
    quotes = publicationReviews,
    className = '',
    onViewAllClick
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isUserScrolling, setIsUserScrolling] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    let scrollTimeout: NodeJS.Timeout;

    const getVisibleCards = () => {
        if (typeof window === 'undefined') return 1;
        if (window.innerWidth >= 1024) return 4; // lg breakpoint
        if (window.innerWidth >= 768) return 2; // md breakpoint
        return 1; // mobile
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => {
            // User is scrolling
            setIsUserScrolling(true);

            // Update current index based on scroll position
            const currentScroll = scrollContainer.scrollLeft;
            const cardWidth = scrollContainer.querySelector('div')?.offsetWidth || 0;
            const gap = 24;
            const totalWidth = cardWidth + gap;
            const newIndex = Math.round(currentScroll / totalWidth);
            setCurrentIndex(newIndex);

            // Clear the previous timeout
            clearTimeout(scrollTimeout);

            // Set a timeout to detect when scrolling stops
            scrollTimeout = setTimeout(() => {
                setIsUserScrolling(true);
            }, 150);
        };

        const getNextScrollPosition = () => {
            const currentScroll = scrollContainer.scrollLeft;
            const cardWidth = scrollContainer.querySelector('div')?.offsetWidth || 0;
            const gap = 24;
            const totalWidth = cardWidth + gap;
            const visibleCards = getVisibleCards();

            // Calculate the maximum scroll position needed to show all cards
            const maxScrollPosition = (quotes.length - visibleCards) * totalWidth;

            // Find the current card index based on scroll position
            const currentIndex = Math.round(currentScroll / totalWidth);

            // If we're at or past the last possible scroll position, return to start
            if (currentScroll >= maxScrollPosition) {
                return 0;
            }

            // Otherwise, go to next card
            return totalWidth * (currentIndex + 1);
        };

        const handleResize = () => {
            // Recalculate current index on resize
            const currentScroll = scrollContainer.scrollLeft;
            const cardWidth = scrollContainer.querySelector('div')?.offsetWidth || 0;
            const gap = 24;
            const totalWidth = cardWidth + gap;
            const newIndex = Math.round(currentScroll / totalWidth);
            setCurrentIndex(newIndex);
        };

        // Add event listeners
        scrollContainer.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        // Auto-scroll interval
        const interval = setInterval(() => {
            if (!isUserScrolling) {
                const nextPosition = getNextScrollPosition();
                const visibleCards = getVisibleCards();
                const maxIndex = quotes.length - visibleCards;

                scrollContainer.scrollTo({
                    left: nextPosition,
                    behavior: 'smooth'
                });

                // Update progress based on visible cards
                const newIndex = Math.round(nextPosition / (scrollContainer.querySelector('div')?.offsetWidth || 0 + 24));
                setCurrentIndex(Math.min(newIndex, maxIndex));
            }
        }, 6000);

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            clearInterval(interval);
            clearTimeout(scrollTimeout);
        };
    }, [quotes.length, isUserScrolling]);

    // Calculate progress based on visible cards
    const progressPercentage = () => {
        const visibleCards = getVisibleCards();
        const maxIndex = quotes.length - visibleCards;
        // Add a minimum of 5% progress when at the start
        return currentIndex === 0 ? 5 : (currentIndex / maxIndex) * 100;
    };

    return (
        <section className={`w-full ${className}`}>
            <div className="module_container">
                <div className="flex flex-col gap-4 pl-2">
                    <div className="flex justify-between items-start mb-0">
                        <h2 className="text-4xl md:text-5xl font-headline text-navy-900">{title}</h2>
                        {/* <button
                            onClick={onViewAllClick}
                            className="text-navy-900 hover:text-orange transition-colors flex items-center gap-2"
                        >
                            All reviews
                            <span className="text-xl">→</span>
                        </button> */}
                    </div>

                    {/* Rating and Review Count */}
                    <div className="flex items-start gap-2 mb-12 flex-col pl-1">
                        <div className="flex gap-1">
                            {[...Array(stats.rating)].map((_, i) => (
                                <Star
                                    key={i}
                                    className="w-6 h-6 text-kibuBlue fill-kibuBlue"
                                />
                            ))}
                            {[...Array(5 - stats.rating)].map((_, i) => (
                                <Star
                                    key={i}
                                    className="w-6 h-6 text-gray-300 fill-gray-300"
                                />
                            ))}
                        </div>
                        <span className="text-gray-600 font-body">
                            {/* based on {stats.totalReviews.toLocaleString()} customer reviews */}
                            Loved by parents and kids!
                        </span>
                    </div>
                </div>

                {/* Review Quotes Scrollable Container */}
                <div className="relative w-full overflow-visible">
                    <div
                        ref={scrollContainerRef}
                        className="flex gap-6 overflow-x-auto snap-x snap-mandatory hide-scrollbar smooth-scroll pr-6 md:pr-10"
                        style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}
                    >
                        {quotes.map((quote, index) => (
                            <div
                                key={index}
                                className="flex-none w-[calc(85vw-3rem)] md:w-72 bg-gray-50 p-8 rounded-lg flex flex-col justify-between gap-8 snap-start"
                            >
                                <p className="text-lg text-navy-900 font-body">“{quote.text}”</p>
                                <div className="flex items-center gap-2">
                                    <img
                                        src={quote.publication.logo}
                                        alt={quote.publication.name}
                                        className="max-h-7 max-w-48 object-contain"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full h-1 bg-white mt-6 rounded-full">
                        <div
                            className="h-full bg-kibuBlue transition-all duration-300 ease-out rounded-full"
                            style={{
                                width: `${Math.min(progressPercentage(), 100)}%`
                            }}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ReviewsModule; 